import { useMerchantBrandingStore } from '@/store/merchantBrandingStore';
import {
  buildCreditCardVuetifyOverrides,
  CREDIT_CARD_CONTRAST_MARKER,
  readMerchantBrandColor,
} from '@/utils/colorContrast';

const CREDIT_CARD_WRAPPER_SELECTOR: string = 'authvia-credit-card-wrapper';

/**
 * payment-method-action injects brand background + white text on disabled submit.
 * Contrast fix is label color only; disabled bg stays on --button-disabled-color.
 */
const CONFLICTING_DISABLED_SUBMIT_RULE: RegExp =
  /\.credit-card-form-spacing\s+\.avwc-authvia-credit-card-form__root\s+\.v-btn\.v-btn--disabled:not\(\.v-btn-toggle\s+\.v-btn\)\s*\{[^}]*\}/g;

export function sanitizeMergedVuetifyOverrides(css: string): string {
  return css.replace(CONFLICTING_DISABLED_SUBMIT_RULE, '').trim();
}

/** Remove Lock contrast CSS previously merged into data-vuetify-overrides. */
export function stripCreditCardContrastOverrides(css: string): string {
  let trimmed: string = css.trim();

  while (trimmed.includes(CREDIT_CARD_CONTRAST_MARKER)) {
    const markerIndex: number = trimmed.indexOf(CREDIT_CARD_CONTRAST_MARKER);
    trimmed = trimmed.slice(0, markerIndex).trim();
  }

  return sanitizeMergedVuetifyOverrides(trimmed);
}

function resolveContrastCss(wrapper: HTMLElement): string {
  const store = useMerchantBrandingStore();
  if (store.creditCardVuetifyOverrides) {
    return store.creditCardVuetifyOverrides;
  }

  const brandColor: string | undefined =
    wrapper.getAttribute('data-color')?.trim() || readMerchantBrandColor();

  return brandColor ? buildCreditCardVuetifyOverrides(brandColor) : '';
}

function mergeVuetifyOverrides(
  existing: string | null,
  contrastCss: string,
): string {
  const baseCss: string = stripCreditCardContrastOverrides((existing ?? '').trim());
  if (!contrastCss) {
    return baseCss;
  }
  if (!baseCss) {
    return contrastCss;
  }
  if (baseCss.includes(contrastCss)) {
    return baseCss;
  }
  return `${baseCss}\n${contrastCss}`;
}

/** Apply contrast CSS via `data-vuetify-overrides` on one credit-card wrapper. */
export function syncCreditCardWrapperOverrides(wrapper: HTMLElement): void {
  const contrastCss: string = resolveContrastCss(wrapper);
  const existing: string | null = wrapper.getAttribute('data-vuetify-overrides');

  if (!contrastCss) {
    const stripped: string = stripCreditCardContrastOverrides(existing ?? '');
    if (stripped) {
      if (existing !== stripped) {
        wrapper.setAttribute('data-vuetify-overrides', stripped);
      }
      return;
    }

    if (existing !== null) {
      wrapper.removeAttribute('data-vuetify-overrides');
    }
    return;
  }

  const merged: string = mergeVuetifyOverrides(existing, contrastCss);
  if (existing !== merged) {
    wrapper.setAttribute('data-vuetify-overrides', merged);
  }
}

/** Sync every credit-card wrapper (wallet, PM dialog, business process). */
export function syncAllCreditCardVuetifyOverrides(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll(CREDIT_CARD_WRAPPER_SELECTOR).forEach((node: Element) => {
    syncCreditCardWrapperOverrides(node as HTMLElement);
  });
}

/** Watch for dynamically mounted credit-card wrappers and apply overrides. */
export function startCreditCardVuetifyOverridesObserver(): () => void {
  syncAllCreditCardVuetifyOverrides();

  const observer = new MutationObserver(() => {
    syncAllCreditCardVuetifyOverrides();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  return () => observer.disconnect();
}
